import { converter, formatCss, parse } from 'culori';

/**
 * Converts a hex color (`#rrggbb`) to four CSS-ready OKLCH strings used to
 * override the `--color-primary*` tokens at runtime. Surfaces and semantic
 * colors are not derived here — they're platform-fixed.
 *
 * The hover variant darkens lightness by 5%; the subtle variant lightens
 * to ~96% with a damped chroma so it remains a tinted surface, not the
 * primary itself. The foreground picks white or near-black based on
 * whichever has stronger perceptual contrast against the input.
 */
const toOklch = converter('oklch');
const toLrgb = converter('lrgb');

export interface PrimaryPalette {
  primary: string;
  primaryHover: string;
  primaryFg: string;
  primarySubtle: string;
}

export function paletteFromHex(hex: string): PrimaryPalette | null {
  const rgb = parse(hex);
  if (!rgb) return null;
  const oklch = toOklch(rgb);
  if (!oklch) return null;

  const l = oklch.l ?? 0.5;
  const c = oklch.c ?? 0.1;
  const h = oklch.h ?? 250;

  const primary = formatCss({ mode: 'oklch', l, c, h });
  const primaryHover = formatCss({
    mode: 'oklch',
    l: Math.max(0.1, l - 0.05),
    c,
    h,
  });
  const primarySubtle = formatCss({
    mode: 'oklch',
    l: 0.96,
    c: Math.min(c, 0.05),
    h,
  });
  const primaryFg = pickForeground(hex);

  return { primary, primaryHover, primaryFg, primarySubtle };
}

/** Picks a foreground color by relative luminance. */
function pickForeground(hex: string): string {
  const rgb = toLrgb(parse(hex));
  if (!rgb) return 'oklch(99% 0 0)';
  const luminance = 0.2126 * (rgb.r ?? 0) + 0.7152 * (rgb.g ?? 0) + 0.0722 * (rgb.b ?? 0);
  return luminance > 0.5 ? 'oklch(20% 0.01 250)' : 'oklch(99% 0 0)';
}

/**
 * Applies a palette to a DOM element by setting CSS custom properties.
 * Pass `document.documentElement` for global; pass a wrapper element to
 * scope the override to e.g. an email-preview or super-admin handoff banner.
 */
export function applyPalette(target: HTMLElement, palette: PrimaryPalette): void {
  target.style.setProperty('--color-primary', palette.primary);
  target.style.setProperty('--color-primary-hover', palette.primaryHover);
  target.style.setProperty('--color-primary-fg', palette.primaryFg);
  target.style.setProperty('--color-primary-subtle', palette.primarySubtle);
}

/** Inverse of `applyPalette` — wipes the override so platform defaults take over. */
export function clearPalette(target: HTMLElement): void {
  target.style.removeProperty('--color-primary');
  target.style.removeProperty('--color-primary-hover');
  target.style.removeProperty('--color-primary-fg');
  target.style.removeProperty('--color-primary-subtle');
}
